import { useRef, useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import { useNavigate } from "react-router-dom";
import { hentBlokkData, stageDot, fmtDue } from "./blokkData";

const RUTE = {
  speakers: (id) => `/admin/speakers?aapne=${id}`,
  partners: (id) => `/admin/partners?aapne=${id}`,
  tasks: (id) => `/admin/saker?aapne=${id}`,
  users: () => "/admin/saker",
};

function MentionChip({ node }) {
  const { kilde, eid, label, bilde } = node.attrs;
  const [prev, setPrev] = useState(null);
  const [vis, setVis] = useState(false);
  const timer = useRef(null);
  const navigate = useNavigate();

  const enter = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setVis(true);
      if (prev) return;
      try {
        const spec = kilde === "tasks"
          ? { bid: "pv", typ: "saker", modus: "kuratert", ids: [eid], kunAapne: false }
          : { bid: "pv", typ: "entitet", kilde, entityId: eid };
        const rows = await hentBlokkData(spec, null);
        setPrev(rows[0] || { tom: true });
      } catch {
        setPrev({ tom: true });
      }
    }, 280);
  };
  const leave = () => { clearTimeout(timer.current); setVis(false); };
  const due = prev && !prev.tom ? fmtDue(prev.frist) : null;

  return (
    <NodeViewWrapper as="span" className="dok-mention-wrap" contentEditable={false}>
      <button type="button" className={`dok-mention${bilde ? " har-img" : ""}`}
        onMouseEnter={enter} onMouseLeave={leave}
        onClick={() => RUTE[kilde] && navigate(RUTE[kilde](eid))}
        data-testid={`dok-mention-${eid}`}>
        {bilde ? <img className="m-img" src={bilde} alt="" /> : <span className="m-at">@</span>}
        <span className="m-navn">{label}</span>
      </button>
      {vis && prev && !prev.tom && (
        <span className="dok-mention-kort" data-testid="dok-mention-kort"
          onMouseEnter={() => clearTimeout(timer.current)} onMouseLeave={leave}>
          {(bilde || prev.bilde || prev.eierBilde) && (
            <img className="m-kortimg" src={bilde || prev.bilde || prev.eierBilde} alt="" />
          )}
          <span className="navn">{prev.navn}{prev.under && prev.under !== prev.navn ? <i> — {prev.under}</i> : null}</span>
          <span className="rad">
            <b className="dot" style={{ background: stageDot(prev.stage) }} />
            {prev.stageLabel}
            {prev.ansvarlig ? ` · ${prev.ansvarlig}` : " · Unassigned"}
            {due ? ` · ${due.label}` : ""}
          </span>
          {prev.nesteSteg && <span className="neste">Next — {prev.nesteSteg}</span>}
        </span>
      )}
    </NodeViewWrapper>
  );
}

export const MentionNode = Node.create({
  name: "mention",
  inline: true,
  group: "inline",
  atom: true,
  selectable: false,
  addAttributes: () => ({
    kilde: { default: "", parseHTML: (el) => el.getAttribute("data-kilde") || "", renderHTML: (a) => ({ "data-kilde": a.kilde }) },
    eid: { default: "", parseHTML: (el) => el.getAttribute("data-eid") || "", renderHTML: (a) => ({ "data-eid": a.eid }) },
    label: { default: "", parseHTML: (el) => el.getAttribute("data-label") || "", renderHTML: (a) => ({ "data-label": a.label }) },
    bilde: { default: "", parseHTML: (el) => el.getAttribute("data-bilde") || "", renderHTML: (a) => ({ "data-bilde": a.bilde }) },
  }),
  parseHTML: () => [{ tag: "span[data-mention]" }],
  renderHTML: ({ HTMLAttributes, node }) => {
    const barn = node.attrs.bilde
      ? [["img", { class: "m-img", src: node.attrs.bilde, alt: "" }]]
      : [["span", { class: "m-at" }, "@"]];
    return ["span",
      mergeAttributes({ "data-mention": "", class: `dok-mention${node.attrs.bilde ? " har-img" : ""}` }, HTMLAttributes),
      ...barn, ["span", { class: "m-navn" }, node.attrs.label]];
  },
  addNodeView: () => ReactNodeViewRenderer(MentionChip),
});
